/** Minimal booking shape for date logic; dates are Zero date numbers. */
export interface BookingSpan {
  id: string;
  vehicleID: string;
  startDate: number;
  endDate: number;
}

/** Inclusive day-range overlap. */
export function bookingsOverlap(a: BookingSpan, b: BookingSpan) {
  return a.startDate <= b.endDate && b.startDate <= a.endDate;
}

/**
 * For every booking, the other bookings of the same vehicle that overlap it.
 * Bookings without conflicts are not in the map.
 */
export function findBookingConflicts<T extends BookingSpan>(
  bookings: T[],
): Map<string, T[]> {
  const conflicts = new Map<string, T[]>();
  const byVehicle = new Map<string, T[]>();
  for (const booking of bookings) {
    const list = byVehicle.get(booking.vehicleID) ?? [];
    list.push(booking);
    byVehicle.set(booking.vehicleID, list);
  }
  for (const list of byVehicle.values()) {
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        if (!bookingsOverlap(list[i], list[j])) continue;
        conflicts.set(list[i].id, [
          ...(conflicts.get(list[i].id) ?? []),
          list[j],
        ]);
        conflicts.set(list[j].id, [
          ...(conflicts.get(list[j].id) ?? []),
          list[i],
        ]);
      }
    }
  }
  return conflicts;
}

/** Upcoming (ends today or later, soonest first) and past (latest first). */
export function splitUpcomingAndPastBookings<T extends BookingSpan>(
  bookings: T[],
  today: number,
) {
  const upcoming = bookings
    .filter((booking) => booking.endDate >= today)
    .sort((a, b) => a.startDate - b.startDate);
  const past = bookings
    .filter((booking) => booking.endDate < today)
    .sort((a, b) => b.startDate - a.startDate);
  return { upcoming, past };
}

/**
 * Assigns overlapping bookings of one row to separate lanes so their bars do
 * not cover each other. Returns lane per booking id and the lane count.
 */
export function assignBookingLanes<T extends BookingSpan>(bookings: T[]) {
  const sorted = [...bookings].sort(
    (a, b) => a.startDate - b.startDate || b.endDate - a.endDate,
  );
  const laneEnds: number[] = [];
  const lanes = new Map<string, number>();
  for (const booking of sorted) {
    let lane = laneEnds.findIndex((end) => end < booking.startDate);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(booking.endDate);
    } else {
      laneEnds[lane] = booking.endDate;
    }
    lanes.set(booking.id, lane);
  }
  return { lanes, laneCount: Math.max(1, laneEnds.length) };
}

export const DAY_MS = 86_400_000;

/** "YYYY-MM-DD" → Zero date number (UTC midnight), or null if invalid */
export function isoToZeroDate(value: string | null | undefined) {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return Date.UTC(year, month - 1, day);
}
